import PropTypes from 'prop-types';
import React from 'react';
import pluralize from 'pluralize';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { Icon } from '@anyroad/icon';
import { colors } from '@anyroad/theme';
import { Select } from '@anyroad/select';
import { components } from 'react-select-new';
import { t } from 'services/translation';

import Hint from './hint';
import { timezoneList } from 'ui_system/utilities/timezoneUtils';
import { hasSpecialPricing } from './Helpers';

import './index.scss';

/* -------------------------------------------------------
 * Styled Components
 * ----------------------------------------------------- */

const Label = styled.label`
  font-size: 14px;
  color: ${colors.text.secondary};
  font-weight: 400;
  line-height: 100%;
`;

const CheckmarkIcon = styled.i`
  color: ${colors.white};
  border: 0;
  left: 3px;
  top: 3px;
`;

const DisplayTime = styled.div`
  font-size: 14px;
  line-height: 18px;
  font-weight: 400;
  color: ${colors.text.primary};
`;

const FieldWrapper = styled.div`
  margin: 24px 0px;
`;

const TimeSlotGrid = styled.div`
  display: grid;
  gap: 16px;

  margin-top: 4px;
  /* Adaptive layout replacing Semantic UI grid */
  grid-template-columns: repeat(auto-fit, minmax(144px, 1fr));
`;

const DropdownWrapper = styled.div`
  width: 185px;
`;

const TimezoneLabel = styled.div`
  color: ${colors.black};
  margin-bottom: 16px;

  span {
    color: ${colors.primary};
    cursor: pointer;
  }
`;

/* Reset button to avoid inherited styles */
const StyledButton = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  max-width: 144px;
  cursor: ${(props) => (props.isSoldOut ? 'not-allowed' : 'pointer')};
  text-align: left;
`;

const TimeSlotContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 54px;
  max-width: 144px;
  padding: 12px 12px;
  border: ${(props) =>
    props.isSoldOut
      ? `1px dashed ${colors.borderAction}`
      : `1px solid ${colors.borderAction}`};
  background: ${(props) => (props.isSoldOut ? 'none' : colors.white)};
  gap: 12px;
  box-sizing: border-box;
`;

const IconCircle = styled.i`
  width: 22px;
  height: 22px;
  background: ${colors.background.light};
  border: 1px solid ${colors.borderAction};
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;

  &.selected {
    background: ${(props) => props.brandColor || colors.primary};
    border-color: ${(props) => props.brandColor || colors.primary};
  }
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const TimeSlotRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
`;

const HintRow = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${colors.text.secondary};
`;

const SpecialPricingIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TimeOptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 12px 16px;
  opacity: ${(props) => (props.isDisabled ? 0.4 : 1)};

  @media (max-width: 768px) {
    padding: 14px 16px;
  }
`;

const TimeOptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TimeOptionLabel = styled.span`
  color: ${(props) => (props.isSelected ? colors.white : colors.text.primary)};
  font-size: 16px;
  font-weight: 500;

  [class*='option']:active & {
    color: ${colors.white};
  }
`;

const TimeOptionSubLabel = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${(props) =>
    props.isSelected ? colors.white : colors.text.secondary};

  [class*='option']:active & {
    color: ${colors.white};
  }
`;

const TimeOptionIconWrapper = styled.span`
  display: flex;
  align-items: center;

  [class*='option']:active & svg,
  [class*='option']:active & svg path {
    stroke: ${colors.white};
  }
`;

const SingleTimeDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const SingleTimeLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  color: ${colors.text.primary};
`;

const SingleTimeHint = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: ${colors.text.secondary};
`;

/* -------------------------------------------------------
 * Utility Functions
 * ----------------------------------------------------- */

const PLACES_LEFT_THRESHOLD = 8;

const sortTimes = (a, b) => {
  const offset = (time) => {
    if (time.match(/.*pm/i)) {
      if (!time.trim().match(/^12/)) {
        return 12;
      }
    } else if (time.match(/.*am/i)) {
      if (time.trim().match(/^12/)) {
        return -12;
      }
    }
    return 0;
  };
  const toFloat = (time) =>
    parseFloat(time.replace(':', '.').replace(/[^\d.-]/g, '')) + offset(time);
  return toFloat(a) - toFloat(b);
};

// Takes a time string and returns the ISO/24-hour time
export const toISOTime = (time) => {
  return moment(time, 'h:mma').format('HH:mm');
};

/* -------------------------------------------------------
 * Main Component
 * ----------------------------------------------------- */

class Times extends React.PureComponent {
  static displayName = 'BookingRequest-Times';

  static propTypes = {
    times: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    guests: PropTypes.number.isRequired,
    showPlacesLeft: PropTypes.bool,
    displaySingleTime: PropTypes.bool,
    value: PropTypes.string,
    errors: PropTypes.array,
    forceDropdown: PropTypes.bool,
    hideLabel: PropTypes.bool,
    isoTime: PropTypes.bool,
    timezonesEnabled: PropTypes.bool,
    timezone: PropTypes.string,
    openTimezoneModal: PropTypes.func,
    hasTicketCapacityEnabled: PropTypes.bool,
    pricingRules: PropTypes.object,
  };

  static defaultProps = {
    time: null,
    errors: [],
    displaySingleTime: true,
    forceDropdown: false,
    hideLabel: false,
    isoTime: false,
    timezonesEnabled: false,
    timezone: '',
    openTimezoneModal: () => {},
    hasTicketCapacityEnabled: false,
    pricingRules: null,
  };

  /* ------------------------ Dropdown Helpers ------------------------ */
  subLabelsForDropdown = () => {
    const subLabels = {};
    let spotsLeft = null;
    const _this = this;

    this._timeKeys().forEach((time) => {
      spotsLeft = _this.props.times[time];
      const timeTrimmed = time.trim();
      if (spotsLeft <= 0) {
        subLabels[timeTrimmed] = `${t('SOLD_OUT')}`;
      } else if (spotsLeft < PLACES_LEFT_THRESHOLD) {
        subLabels[timeTrimmed] =
          `${spotsLeft} ${t(pluralize('spot', spotsLeft))}
            ${t('left')}`;
      }
    });

    return subLabels;
  };

  prepareSelectOptions = () => {
    const { pricingRules, isoTime } = this.props;

    return this._timeKeys()
      .sort(sortTimes)
      .map((time) => {
        let label;
        const timeTrimmed = time.trim();

        if (isoTime) {
          label = toISOTime(time);
        } else {
          label = time.toUpperCase();
          label = label.replace('AM', ` ${t('AM')}`);
          label = label.replace('PM', ` ${t('PM')}`);
        }

        const isSoldOut = this._isSoldOut(this.props.times[time]);
        const showPricingIcon = hasSpecialPricing(time, pricingRules);

        return {
          value: time,
          label,
          timeTrimmed,
          showPricingIcon,
          isDisabled: isSoldOut,
        };
      });
  };

  CustomOption = (props) => {
    const { data, isSelected, isFocused, isDisabled } = props;
    const subLabels = this.subLabelsForDropdown();
    const isMobile = window.innerWidth <= 768;
    const shouldUseWhiteText =
      isSelected || (!isMobile && isFocused && !isDisabled);

    return (
      <components.Option {...props}>
        <TimeOptionWrapper isDisabled={data.isDisabled}>
          <TimeOptionRow>
            <TimeOptionLabel isSelected={shouldUseWhiteText}>
              {data.label}
            </TimeOptionLabel>
            {data.showPricingIcon && (
              <TimeOptionIconWrapper>
                <Icon
                  name="Tag"
                  stroke={shouldUseWhiteText ? colors.white : undefined}
                  width={14}
                  height={14}
                />
              </TimeOptionIconWrapper>
            )}
          </TimeOptionRow>
          {subLabels[data.timeTrimmed] && (
            <TimeOptionSubLabel isSelected={shouldUseWhiteText}>
              {subLabels[data.timeTrimmed]}
            </TimeOptionSubLabel>
          )}
        </TimeOptionWrapper>
      </components.Option>
    );
  };

  CustomSingleValue = (props) => {
    const { data } = props;

    return (
      <components.SingleValue {...props}>
        <TimeOptionRow>
          <TimeOptionLabel isSelected={false}>{data.label}</TimeOptionLabel>
          {data.showPricingIcon && <Icon name="Tag" width={14} height={14} />}
        </TimeOptionRow>
      </components.SingleValue>
    );
  };

  getCustomSelectStyles = () => {
    const { brandColor } = this.props;
    const brandColorValue = brandColor || colors.primary;
    const isMobile = window.innerWidth <= 768;

    return {
      menuPortal: (styles) => ({
        ...styles,
        zIndex: 9999,
      }),
      menu: (styles) => ({
        ...styles,
        borderRadius: isMobile ? '4px' : '0px 0px 4px 4px',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        marginTop: 0,
        marginBottom: isMobile ? '24px' : '16px',
      }),
      menuList: (styles) => ({
        ...styles,
        maxHeight: isMobile ? '300px' : '200px',
        padding: 0,
        overflowY: 'auto',
      }),
      option: (styles, { isSelected, isDisabled, isFocused }) => {
        let backgroundColor = colors.white;

        if (isSelected || (!isMobile && isFocused && !isDisabled)) {
          backgroundColor = brandColorValue;
        }

        return {
          ...styles,
          height: 'auto',
          padding: 0,
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor,
          color: 'inherit',
          boxShadow: 'none',
          '&:last-child': {
            borderBottom: 'none',
          },
          '&:active': {
            backgroundColor: isDisabled ? colors.white : brandColorValue,
            color: 'inherit',
          },
        };
      },
    };
  };

  /* ------------------------ Lifecycle ------------------------ */

  componentDidMount() {
    this._selectSingleTime();
  }

  componentDidUpdate(prevProps) {
    const { date, times, value } = this.props;
    const { date: prevDate, times: prevTimes } = prevProps;

    if (date && prevDate && times) {
      if (date !== prevDate) {
        if (Object.keys(times).length <= 1) {
          this._selectSingleTime();
        } else {
          this.props.onChange(this.props.name, null);
        }
      }
    } else {
      this._selectSingleTime();
    }

    // Auto-deselect if the selected time becomes sold out
    if (value && times && times[value] !== undefined) {
      const placesLeft = times[value];
      if (this._isSoldOut(placesLeft)) {
        // Only clear if times have changed to avoid infinite loops
        if (prevTimes && prevTimes[value] !== placesLeft) {
          this.props.onChange(this.props.name, null);
        }
      }
    }

    if (this.props.errors.length > 0) {
      $(this.refs.label).transition({
        animation: 'shake',
        duration: '600ms',
      });
    }
  }

  /* ------------------------ Helpers ------------------------ */

  onTimeClick = (e) => {
    let { value } = e.currentTarget.attributes['data-value'];
    if (value === this.props.value) {
      value = null;
    }
    this.props.onChange(this.props.name, value);
  };

  _selectSingleTime = () => {
    const allTimes = this._timeKeys();
    if (allTimes.length === 1) {
      const time = allTimes[0];
      const placesLeft = this.props.times[time];
      if (!this._isSoldOut(placesLeft)) {
        this.props.onChange(this.props.name, time);
      }
    }
  };

  _isSoldOut = (placesLeft) => {
    return placesLeft < this.props.guests;
  };

  _timeKeys = () => {
    return Object.keys(this.props.times);
  };

  _hasMultipleTimes = () => {
    return this.props.times && this._timeKeys().length > 1;
  };

  _renderTranslatedTime = (time) => {
    time = time.trim();
    const onlyTime = time.slice(0, -2); // 9:00
    const am_pm = time.slice(-2); // am or pm
    return onlyTime + t(am_pm);
  };

  /* ------------------------ Render Helpers ------------------------ */

  renderTimeSlot = (time, index) => {
    const { pricingRules, isoTime, showPlacesLeft, value, brandColor } =
      this.props;

    const placesLeft = this.props.times[time];
    if (placesLeft == null) return null;

    const isSelected = value === time;
    const isSoldOut = this._isSoldOut(placesLeft);
    const showPricingIcon = hasSpecialPricing(time, pricingRules);

    let displayTime = isoTime
      ? toISOTime(time)
      : this._renderTranslatedTime(time);

    return (
      <StyledButton
        key={index}
        data-value={time}
        isSoldOut={isSoldOut}
        onClick={!isSoldOut ? this.onTimeClick : undefined}
        data-testid="time-wrapper"
      >
        <TimeSlotContainer
          data-testid="start-time-control"
          isSoldOut={isSoldOut}
        >
          <IconCircle
            brandColor={brandColor}
            className={isSelected ? 'selected' : ''}
            data-testid="time-slot"
          >
            {isSelected && <CheckmarkIcon className="af-checkmark" />}
          </IconCircle>
          <TextColumn>
            <TimeSlotRow>
              <DisplayTime>{displayTime}</DisplayTime>
              {showPricingIcon && (
                <Icon
                  name="Tag"
                  width={14}
                  height={14}
                  style={{ marginLeft: 6 }}
                />
              )}
            </TimeSlotRow>
            <HintRow>
              <Hint
                spots={Math.max(placesLeft, 0)}
                isSoldOut={isSoldOut}
                showPlacesLeft={showPlacesLeft}
              />
            </HintRow>
          </TextColumn>
        </TimeSlotContainer>
      </StyledButton>
    );
  };

  renderTimeSlotGrid = () => {
    return (
      <TimeSlotGrid data-testid="time-grid">
        {this._timeKeys().sort(sortTimes).map(this.renderTimeSlot)}
      </TimeSlotGrid>
    );
  };

  _renderSingleTime = (time) => {
    const { isoTime, showPlacesLeft } = this.props;
    const placesLeft = this.props.times[time];

    let displayTime = this._renderTranslatedTime(time);
    if (isoTime) {
      displayTime = toISOTime(time);
    }

    return (
      <SingleTimeDisplay>
        <SingleTimeLabel>{displayTime}</SingleTimeLabel>
        <SingleTimeHint>
          <Hint
            spots={placesLeft >= 0 ? placesLeft : 0}
            isSoldOut={this._isSoldOut(placesLeft)}
            showPlacesLeft={showPlacesLeft}
          />
        </SingleTimeHint>
      </SingleTimeDisplay>
    );
  };

  /* -------------------------------------------------------
   * Main Render
   * ----------------------------------------------------- */

  render() {
    const {
      timezone,
      openTimezoneModal,
      timezonesEnabled,
      hideLabel,
      date,
      displaySingleTime,
      forceDropdown,
    } = this.props;

    const timesCount = this._timeKeys().length;
    const showTimezone = timezonesEnabled && timesCount > 0;

    let labelText = t('SELECT_THE_DESIRED_START_TIME');
    let view;

    if (timesCount === 0) {
      view = <p>{t('SELECT_A_DATE_ABOVE_TO_SEE_AVAILABLE_TIMES')}</p>;
    } else if (!forceDropdown && timesCount === 1 && displaySingleTime) {
      // Scenario 1: Single time rendered as text (1 time option)
      // ===========
      labelText = t('START_TIME');
      view = <div>{this._renderSingleTime(this._timeKeys()[0])}</div>;
    } else if (!forceDropdown && timesCount === 1 && !displaySingleTime) {
      // Scenario 1b: Single time but displaySingleTime is false - render nothing
      // ===========
      return null;
    } else if (!forceDropdown && timesCount <= 6) {
      // Scenario 2: Grid view of timeslots (2 - 6 time options)
      // ===========
      view = this.renderTimeSlotGrid();
    } else {
      // Scenario 3: Dropdown select for timeslots (7+ time options)
      // ===========
      const selectOptions = this.prepareSelectOptions();
      const selectedOption = selectOptions.find(
        (option) => option.value === this.props.value
      );
      const isMobile = window.innerWidth <= 768;

      view = (
        <DropdownWrapper data-testid="time-dropdown">
          <Select
            isSearchable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuPlacement={isMobile ? 'top' : 'auto'}
            onChange={(selectedOption) =>
              this.props.onChange(
                this.props.name,
                selectedOption?.value || null
              )
            }
            placeholder={t('SELECT')}
            options={selectOptions}
            value={selectedOption}
            components={{
              Option: this.CustomOption,
              SingleValue: this.CustomSingleValue,
            }}
            styles={this.getCustomSelectStyles()}
          />
        </DropdownWrapper>
      );
    }

    const selectedTimezone = timezoneList[timezone];

    return (
      <FieldWrapper data-testid="time-field">
        {!hideLabel && <Label ref="label">{labelText}</Label>}

        {showTimezone && (
          <TimezoneLabel>
            {t('TIMES_SHOWN_IN')}{' '}
            <span onClick={openTimezoneModal}>{selectedTimezone.name}</span>
          </TimezoneLabel>
        )}

        {view}
      </FieldWrapper>
    );
  }
}

export default Times;
